import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Layout from "../layout";
import NotFound from "../componants/NotFound";
import PageSkeleton from "../componants/PageSkeleton";
import PrivateRoute from "./PrivateRoute";

// ── Onboarding ───────────────────────────────────────────────────────────────
const Login = lazy(() => import("../pages/onboarding/Login"));
const ForgotPassword = lazy(() => import("../pages/onboarding/ForgotPassword"));
const OTP = lazy(() => import("../pages/onboarding/OTP"));
const NewPassword = lazy(() => import("../pages/onboarding/NewPassword"));

// ── Super Admin › Dashboard ──────────────────────────────────────────────────
const Dashboard = lazy(() => import("../pages/superAdmin/dashboard/Dashboard"));
const AdminNotification = lazy(() => import("../header/AdminNotification"));
const ReviewRating = lazy(
  () => import("../pages/superAdmin/reviewRating/Review"),
);

// ── Super Admin › Booking ────────────────────────────────────────────────────
const AllBooking = lazy(() => import("../pages/superAdmin/booking/allBooking"));
const Flight = lazy(() => import("../pages/superAdmin/booking/flight/flight"));
const FlightDetail = lazy(
  () => import("../pages/superAdmin/booking/flight/flightDetail"),
);
const Hotel = lazy(() => import("../pages/superAdmin/booking/hotel/hotel"));
const HotelDetail = lazy(
  () => import("../pages/superAdmin/booking/hotel/hotelDetail"),
);
const Event = lazy(() => import("../pages/superAdmin/booking/events/event"));
const EventDetailView = lazy(
  () => import("../pages/superAdmin/booking/events/EventDetailView"),
);
const Venue = lazy(() => import("../pages/superAdmin/booking/venue/Venue"));
const VenueView = lazy(
  () => import("../pages/superAdmin/booking/venue/VenueDetail"),
);
const Rental = lazy(() => import("../pages/superAdmin/booking/rental/rental"));
const RentalDetail = lazy(
  () => import("../pages/superAdmin/booking/rental/RentalDetail"),
);
const Cruises = lazy(
  () => import("../pages/superAdmin/booking/cruise/Cruises"),
);
const CruiseDetail = lazy(
  () => import("../pages/superAdmin/booking/cruise/CruiseDetail"),
);
const Trains = lazy(() => import("../pages/superAdmin/booking/train/Trains"));
const DomesticTrainDetail = lazy(
  () => import("../pages/superAdmin/booking/train/DomesticTrainDetail"),
);
const InternationalTrainDetail = lazy(
  () => import("../pages/superAdmin/booking/train/InternationalTrainDetail"),
);
const Bus = lazy(() => import("../pages/superAdmin/booking/bus/Bus"));
const DomesticBusDetail = lazy(
  () => import("../pages/superAdmin/booking/bus/DomesticBusDetail"),
);
const InternationalBusDetail = lazy(
  () => import("../pages/superAdmin/booking/bus/InternationalBusDetail"),
);
const AirportLounge = lazy(
  () => import("../pages/superAdmin/booking/airportLounge/AirportLounge"),
);
const AirportLoungeDetail = lazy(
  () => import("../pages/superAdmin/booking/airportLounge/AirportLoungeDetail"),
);
const Flightlock = lazy(
  () => import("../pages/superAdmin/booking/flightLock/Flightlock"),
);
const AddConfiguration = lazy(
  () => import("../pages/superAdmin/booking/flightLock/AddConfiguration"),
);

// ── Super Admin › User Management ────────────────────────────────────────────
const UserManagement = lazy(
  () => import("../pages/superAdmin/userManagement/UserManagement"),
);
const UserDetail = lazy(
  () => import("../pages/superAdmin/userManagement/UserDetail"),
);

// ── Super Admin › Coupon Management ─────────────────────────────────────────
const AllCoupon = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/allCoupon/AllCoupon"),
);
const AllCouponDetail = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/allCoupon/AllCouponDetail"),
);
const CreateAllCoupon = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/allCoupon/CreateAllCoupon"),
);
const FlightCoupons = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/flightCoupons/FlightCoupons"),
);
const FlightCouponsDetail = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/flightCoupons/FlightCouponsDetail"),
);
const CreateFlightCoupons = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/flightCoupons/CreateFlightCoupons"),
);
const AirlineOffers = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/airlineOffers/AirlineOffers"),
);
const AirlineDetail = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/airlineOffers/AirlineDetail"),
);
const CreateAirlineCoupon = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/airlineOffers/CreateAirlineCoupon"),
);
const BankOffers = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/bankOffers/BankOffers"),
);
const BankOfferDetail = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/bankOffers/BankOfferDetail"),
);
const CreateBankOffer = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/bankOffers/CreateBankOffer"),
);
const BusCoupons = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/busCoupons/BusCoupons"),
);
const BusCouponsDetail = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/busCoupons/BusCouponsDetail"),
);
const CreateBusCopons = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/busCoupons/CreateBusCopons"),
);
const EventCoupon = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/eventCoupon/EventCoupon"),
);
const EventCouponDetail = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/eventCoupon/EventCouponDetail"),
);
const CreateEventCoupon = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/eventCoupon/CreateEventCoupon"),
);
const HotelCoupons = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/hotelCoupon/HotelCoupons"),
);
const HotelCouponDetails = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/hotelCoupon/HotelCouponDetails"),
);
const CreateHotelCoupon = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/hotelCoupon/CreateHotelCoupon"),
);
const AppWebsiteOffers = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/appWebsiteOffers/AppWebsiteOffers"),
);
const AppWebsiteOffersDetail = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/appWebsiteOffers/AppWebsiteOffersDetail"),
);
const CreateAppWebsiteOffers = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/appWebsiteOffers/CreateAppWebsiteOffers"),
);
const CarRentalCoupons = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/carRentalCoupons/CarRentalCoupons"),
);
const CarRentalCouponsDetail = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/carRentalCoupons/CarRentalCouponsDetail"),
);
const CreateCarRentalCoupons = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/carRentalCoupons/CreateCarRentalCoupons"),
);
const CruiseCoupon = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/cruiseCoupon/CruiseCoupon"),
);
const CruiseCouponDetails = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/cruiseCoupon/CruiseCouponDetails"),
);
const CreateCruiseCoupon = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/cruiseCoupon/CreateCruiseCoupon"),
);
const TrainCoupons = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/trainCoupons/TrainCoupons"),
);
const TrainCouponsDetails = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/trainCoupons/TrainCouponsDetails"),
);
const CreateTrainCoupons = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/trainCoupons/CreateTrainCoupons"),
);
const CouponAnalytics = lazy(
  () =>
    import("../pages/superAdmin/couponManagement/allCoupon/couponAnalytics/CouponAnalytics"),
);

// ── Super Admin › Vendor Management ───────────────────────────────────────
const VendorManagement = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/vendorManagementTabs/VendorTab"),
);
const VendorDetailTab = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/vendorManagementDetail/VendorDetailTab"),
);
const VendorProfileEdit = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/vendorManagementDetail/VendorProfileEdit"),
);
const VenueTab = lazy(
  () => import("../pages/superAdmin/vendorManagement/venueVendor/VenueTab"),
);
const VenueDetail = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/venueVendor/venueDetail/VenueDetail"),
);
const VenueProposals = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/venueVendor/venueProposal/VenueProposals"),
);
const VenueProposalDetail = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/venueVendor/venueProposal/VenueProposalDetail"),
);
const EventTab = lazy(
  () => import("../pages/superAdmin/vendorManagement/eventVendor/EventTab"),
);
const EventDetail = lazy(
  () => import("../pages/superAdmin/vendorManagement/eventVendor/EventDetail"),
);
const EventVendorCouponDetail = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/eventVendor/EventCouponDetail"),
);
const EventVendorCouponForm = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/eventVendor/EventCouponForm"),
);
const VendorHotelDetail = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/hotelVendor/VendorHotelDetail"),
);
const HotelTab = lazy(
  () => import("../pages/superAdmin/vendorManagement/hotelVendor/HotelTab"),
);

const CreateLayout = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/venueVendor/editVenueVendor/CreateLayout"),
);

const BasicInfo = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/venueVendor/editVenueVendor/BasicInfo"),
);

const Location = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/venueVendor/editVenueVendor/Location"),
);

const Capacity = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/venueVendor/editVenueVendor/Capacity"),
);

const Amenities = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/venueVendor/editVenueVendor/Amenities"),
);
const BookingDetail = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/venueVendor/editVenueVendor/BookingDetail"),
);
const Rules = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/venueVendor/editVenueVendor/Rules"),
);

const Events = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/venueVendor/editVenueVendor/Events"),
);
const BookingOfParticularVenue = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/venueVendor/venueDetail/BookingsOfParticularVenue"),
);
const VenueBookingDetail = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/venueVendor/venueDetail/VenueBookingsDetail"),
);
const BookingsOfParticularEvent = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/eventVendor/BookingsOfParticularEvent"),
);
const EventBookingDetail = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/eventVendor/EventBookingDetail"),
);
const CreateEventLayout = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/eventVendor/editEventVendor/CreateEventLayout"),
);
const BasicInformation = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/eventVendor/editEventVendor/BasicInformation"),
);
const Media = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/eventVendor/editEventVendor/Media"),
);
const DateAndTime = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/eventVendor/editEventVendor/DateAndTime"),
);
const EventVenueDetail = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/eventVendor/editEventVendor/VenueDetail"),
);
const SeatLayout = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/eventVendor/editEventVendor/SeatLayout"),
);
const TicketSetup = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/eventVendor/editEventVendor/TicketSetup"),
);
const Policies = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/eventVendor/editEventVendor/Policies"),
);

// ── Super Admin › Hotel Vendor ───────────────────────────────────────
const HotelBookingDetail = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/hotelVendor/HotelBookingDetail"),
);
const BookingsOfParticularHotel = lazy(
  () =>
    import("../pages/superAdmin/vendorManagement/hotelVendor/BookingsOfParticularHotel"),
);

// ── Super Admin › Roles & Permissions ───────────────────────────────────────
const ManageRoles = lazy(
  () => import("../pages/superAdmin/rolesPermissions/manageRoles/ManageRoles"),
);

// ── Super Admin › Payment ────────────────────────────────────────────────────
const PaymentTransactions = lazy(
  () =>
    import("../pages/superAdmin/paymentTransactions/payment/PaymentTransactions"),
);
const Refunds = lazy(
  () => import("../pages/superAdmin/paymentTransactions/refund/Refunds"),
);
const RefundsDetail = lazy(
  () => import("../pages/superAdmin/paymentTransactions/refund/RefundsDetail"),
);
const Settlements = lazy(
  () => import("../pages/superAdmin/paymentTransactions/settlement/Settlement"),
);
const SettlementsDetail = lazy(
  () =>
    import("../pages/superAdmin/paymentTransactions/settlement/SettlementDetailModal"),
);
const Revenue = lazy(
  () => import("../pages/superAdmin/paymentTransactions/revenue/RevenueReport"),
);

// ── Super Admin › Reports & Analytics ────────────────────────────────────────────────────
const Report = lazy(
  () => import("../pages/superAdmin/reportsAnalytics/Report"),
);

// ── Super Admin › Support ────────────────────────────────────────────────────
const Support = lazy(() => import("../pages/superAdmin/support/support"));
const SupportDetail = lazy(
  () => import("../pages/superAdmin/support/supportDetail"),
);

// ── Super Admin › Influencer Management ─────────────────────────────────────
const InfluencerDashboard = lazy(
  () =>
    import("../pages/superAdmin/influencerManagement/influencerDashboard/InfluencerDashboard"),
);
const Application = lazy(
  () =>
    import("../pages/superAdmin/influencerManagement/influencerApplication/Application"),
);
const ApplicationDetail = lazy(
  () =>
    import("../pages/superAdmin/influencerManagement/influencerApplication/ApplicationDetail"),
);
const Videos = lazy(
  () =>
    import("../pages/superAdmin/influencerManagement/videoModeration/Videos"),
);

// ── Other Management ───────────────────────────────────────────────────────────────────
const AvatarManagement = lazy(
  () =>
    import("../pages/superAdmin/otherManagement/avatarManagement/AvatarManagement"),
);
const BannerManagement = lazy(
  () =>
    import("../pages/superAdmin/otherManagement/bannerManagement/BannerManagement"),
);

// ── Subscription Management ───────────────────────────────────────────────────────────────────
const SubscriptionPlans = lazy(
  () => import("../pages/superAdmin/subscriptionManagement/SubscriptionPlans"),
);
const Subscribers = lazy(
  () => import("../pages/superAdmin/subscriptionManagement/Subscribers"),
);
const SubscribersDetail = lazy(
  () => import("../pages/superAdmin/subscriptionManagement/SubscribersDetail"),
);
const SubscriptionPlansDetail = lazy(
  () =>
    import("../pages/superAdmin/subscriptionManagement/SubscriptionPlansDetail"),
);
const SubscriptionPlansCreate = lazy(
  () =>
    import("../pages/superAdmin/subscriptionManagement/SubscriptionPlansCreate"),
);

// ── Blog Management ───────────────────────────────────────────────────────────────────
const BlogCategories = lazy(
  () => import("../pages/superAdmin/blogmanagement/category/BlogCategory"),
);
const BlogAuthor = lazy(
  () => import("../pages/superAdmin/blogmanagement/author/BlogAuthor"),
);
const AllBlog = lazy(
  () => import("../pages/superAdmin/blogmanagement/blog/AllBlog"),
);
const BlogDetail = lazy(
  () => import("../pages/superAdmin/blogmanagement/blog/BlogDetail"),
);
const CreateBlog = lazy(
  () => import("../pages/superAdmin/blogmanagement/blog/CreateBlog"),
);

// ── Request Management ────────────────────────────────────────────────────────
const Requests = lazy(
  () => import("../pages/superAdmin/requestManagement/Requests"),
);
const RequestForm = lazy(
  () => import("../pages/superAdmin/requestManagement/RequestForm"),
);
const RequestDetail = lazy(
  () => import("../pages/superAdmin/requestManagement/RequestDetail"),
);

// ── Platform Settings ────────────────────────────────────────────────────────
const PlatformSettings = lazy(
  () => import("../pages/superAdmin/platformSettings/PlatformSettings"),
);

// ── Package Management ────────────────────────────────────────────────────────
const PackageList = lazy(
  () => import("../pages/superAdmin/packageManagement/PackageList"),
);
const PackageDetail = lazy(
  () => import("../pages/superAdmin/packageManagement/PackageDetail"),
);
const PackageVendors = lazy(
  () => import("../pages/superAdmin/packageManagement/PackageVendors"),
);
const VendorPackages = lazy(
  () => import("../pages/superAdmin/packageManagement/VendorPackages"),
);
const CategoryManagement = lazy(
  () => import("../pages/superAdmin/packageManagement/CategoryManagement"),
);
const CreatePackageLayout = lazy(
  () =>
    import("../pages/superAdmin/packageManagement/createPackage/CreatePackageLayout"),
);
const PackageBasicInfo = lazy(
  () => import("../pages/superAdmin/packageManagement/createPackage/BasicInfo"),
);
const PackageMedia = lazy(
  () => import("../pages/superAdmin/packageManagement/createPackage/Media"),
);
const PackageItinerary = lazy(
  () => import("../pages/superAdmin/packageManagement/createPackage/Itinerary"),
);
const PackageInclusionsExclusions = lazy(
  () =>
    import("../pages/superAdmin/packageManagement/createPackage/InclusionsExclusions"),
);
const PackagePricing = lazy(
  () => import("../pages/superAdmin/packageManagement/createPackage/Pricing"),
);
// const PackageAddOns = lazy(
//   () => import("../pages/superAdmin/packageManagement/createPackage/AddOns"),
// );
const PackageCancellationPolicy = lazy(
  () =>
    import("../pages/superAdmin/packageManagement/createPackage/CancellationPolicy"),
);
const MyPackages = lazy(
  () =>
    import("../pages/superAdmin/packageManagement/MyPackages"),
);

// ─────────────────────────────────────────────────────────────────────────────
// Route configs
// ─────────────────────────────────────────────────────────────────────────────

const publicRoutes = [
  { path: "/", element: <Login /> },
  { path: "/forgot-password", element: <ForgotPassword /> },
  { path: "/otp", element: <OTP /> },
  { path: "/set-password", element: <NewPassword /> },
];

const protectedRoutes = [
  // Dashboard
  { path: "/dashboard", element: <Dashboard /> },
  { path: "/notification", element: <AdminNotification /> },
  { path: "/review/:contentType/:id", element: <ReviewRating /> },

  // Bookings
  { path: "/all-bookings", element: <AllBooking /> },
  { path: "/flight", element: <Flight /> },
  { path: "/flight-detail/:id", element: <FlightDetail /> },
  { path: "/hotel", element: <Hotel /> },
  { path: "/hotel-detail/:id", element: <HotelDetail /> },
  { path: "/event", element: <Event /> },
  { path: "/event-detail-view/:id", element: <EventDetailView /> },
  { path: "/venue", element: <Venue /> },
  { path: "/venue-detail-view/:id", element: <VenueView /> },
  { path: "/car-rental", element: <Rental /> },
  { path: "/car-rental-detail/:id", element: <RentalDetail /> },
  { path: "/cruise", element: <Cruises /> },
  { path: "/cruise-detail/:id", element: <CruiseDetail /> },
  { path: "/train", element: <Trains /> },
  { path: "/domestic-train-detail", element: <DomesticTrainDetail /> },
  {
    path: "/international-train-detail",
    element: <InternationalTrainDetail />,
  },
  { path: "/bus", element: <Bus /> },
  { path: "/domestic-bus-detail", element: <DomesticBusDetail /> },
  { path: "/international-bus-detail", element: <InternationalBusDetail /> },
  { path: "/airport-lounge", element: <AirportLounge /> },
  { path: "/airport-lounge-detail", element: <AirportLoungeDetail /> },
  { path: "/flight-lock", element: <Flightlock /> },
  { path: "/add-configuration", element: <AddConfiguration /> },
  { path: "/add-configuration/edit/:id", element: <AddConfiguration /> },

  // User Management
  { path: "/usermanagement", element: <UserManagement /> },
  { path: "/user-detail/:userId", element: <UserDetail /> },

  // Coupon Management
  { path: "/all-coupons", element: <AllCoupon /> },
  { path: "/all-coupons/create", element: <CreateAllCoupon /> },
  { path: "/all-coupons/edit/:id", element: <CreateAllCoupon /> },
  { path: "/all-coupons/detail/:id", element: <AllCouponDetail /> },
  { path: "/flight-coupon", element: <FlightCoupons /> },
  { path: "/flight-coupon/create", element: <CreateFlightCoupons /> },
  { path: "/flight-coupon/edit/:id", element: <CreateFlightCoupons /> },
  { path: "/flight-coupon/detail/:id", element: <FlightCouponsDetail /> },
  { path: "/airline-coupon", element: <AirlineOffers /> },
  { path: "/airline-coupon/create", element: <CreateAirlineCoupon /> },
  { path: "/airline-coupon/edit/:id", element: <CreateAirlineCoupon /> },
  { path: "/airline-coupon/detail/:id", element: <AirlineDetail /> },
  { path: "/bank-offer", element: <BankOffers /> },
  { path: "/bank-offer/create", element: <CreateBankOffer /> },
  { path: "/bank-offer/edit/:id", element: <CreateBankOffer /> },
  { path: "/bank-offer/detail/:id", element: <BankOfferDetail /> },
  { path: "/bus-coupon", element: <BusCoupons /> },
  { path: "/bus-coupon/create", element: <CreateBusCopons /> },
  { path: "/bus-coupon/edit/:id", element: <CreateBusCopons /> },
  { path: "/bus-coupon/detail/:id", element: <BusCouponsDetail /> },
  { path: "/event-coupon", element: <EventCoupon /> },
  { path: "/event-coupon/create", element: <CreateEventCoupon /> },
  { path: "/event-coupon/edit/:id", element: <CreateEventCoupon /> },
  { path: "/event-coupon/detail/:id", element: <EventCouponDetail /> },
  { path: "/hotel-coupon", element: <HotelCoupons /> },
  { path: "/hotel-coupon/create", element: <CreateHotelCoupon /> },
  { path: "/hotel-coupon/edit/:id", element: <CreateHotelCoupon /> },
  { path: "/hotel-coupon/detail/:id", element: <HotelCouponDetails /> },
  { path: "/app-website-offer", element: <AppWebsiteOffers /> },
  { path: "/app-website-offer/create", element: <CreateAppWebsiteOffers /> },
  { path: "/app-website-offer/edit/:id", element: <CreateAppWebsiteOffers /> },
  {
    path: "/app-website-offer/detail/:id",
    element: <AppWebsiteOffersDetail />,
  },
  { path: "/car-rental-coupon", element: <CarRentalCoupons /> },
  { path: "/car-rental-coupon/create", element: <CreateCarRentalCoupons /> },
  { path: "/car-rental-coupon/edit/:id", element: <CreateCarRentalCoupons /> },
  {
    path: "/car-rental-coupon/detail/:id",
    element: <CarRentalCouponsDetail />,
  },
  { path: "/cruise-coupon", element: <CruiseCoupon /> },
  { path: "/cruise-coupon/create", element: <CreateCruiseCoupon /> },
  { path: "/cruise-coupon/edit/:id", element: <CreateCruiseCoupon /> },
  { path: "/cruise-coupon/detail/:id", element: <CruiseCouponDetails /> },
  { path: "/train-coupon", element: <TrainCoupons /> },
  { path: "/train-coupon/create", element: <CreateTrainCoupons /> },
  { path: "/train-coupon/edit/:id", element: <CreateTrainCoupons /> },
  { path: "/train-coupon/detail/:id", element: <TrainCouponsDetails /> },
  { path: "/coupon-analytics", element: <CouponAnalytics /> },

  // Vendor Management
  { path: "/all-vendor", element: <VendorManagement /> },
  { path: "/vendor-detail/:id", element: <VendorDetailTab /> },
  { path: "/vendor-detail", element: <VendorDetailTab /> },
  { path: "/vendor-detail/:id/edit", element: <VendorProfileEdit /> },
  { path: "/venue-vendor", element: <VenueTab /> },
  { path: "/venue-detail/:id", element: <VenueDetail /> },
  { path: "/venue-detail", element: <VenueDetail /> },
  { path: "/venue-proposals", element: <VenueProposals /> },
  { path: "/bookings-venue", element: <BookingOfParticularVenue /> },
  { path: "/venue-booking-detail/:id", element: <VenueBookingDetail /> },
  { path: "/venue-proposal/detail/:id", element: <VenueProposalDetail /> },
  { path: "/event-vendor", element: <EventTab /> },
  { path: "/event-detail/:id", element: <EventDetail /> },
  { path: "/event-coupon-detail/:id", element: <EventVendorCouponDetail /> },
  {
    path: "/event-coupon-form/:eventId/:couponId?",
    element: <EventVendorCouponForm />,
  },
  { path: "/bookings-event", element: <BookingsOfParticularEvent /> },
  { path: "/event-booking-detail/:id", element: <EventBookingDetail /> },
  { path: "/hotel-vendor", element: <HotelTab /> },
  { path: "/vendor-hotel-detail/:id", element: <VendorHotelDetail /> },
  { path: "/hotel-booking-detail/:id", element: <HotelBookingDetail /> },
  { path: "/bookings-hotel", element: <BookingsOfParticularHotel /> },

  // Roles & Permissions
  { path: "/manage-roles", element: <ManageRoles /> },

  // Payment
  { path: "/payment", element: <PaymentTransactions /> },
  { path: "/refund", element: <Refunds /> },
  { path: "/refund-detail", element: <RefundsDetail /> },
  { path: "/settlement", element: <Settlements /> },
  { path: "/settlement-detail", element: <SettlementsDetail /> },
  { path: "/revenue", element: <Revenue /> },

  // Reports & Analytics
  { path: "/report", element: <Report /> },

  // Support
  { path: "/support", element: <Support /> },
  { path: "/support-detail/:id", element: <SupportDetail /> },

  //Other Management
  { path: "/avatar-management", element: <AvatarManagement /> },
  { path: "/banner-management", element: <BannerManagement /> },

  // Influencer Management
  { path: "/influencer-dashboard", element: <InfluencerDashboard /> },
  { path: "/influencer-application", element: <Application /> },
  {
    path: "/influencer-application-detail/:id",
    element: <ApplicationDetail />,
  },
  { path: "/video-moderation", element: <Videos /> },

  // Subscription Managemnent
  { path: "/subscription-plans", element: <SubscriptionPlans /> },
  { path: "/subscribers", element: <Subscribers /> },
  { path: "/subscribers-detail/:id", element: <SubscribersDetail /> },
  {
    path: "/subscription-plans-detail/:id",
    element: <SubscriptionPlansDetail />,
  },
  { path: "/subscription-plans-create", element: <SubscriptionPlansCreate /> },
  {
    path: "/subscription-plans-create/:id",
    element: <SubscriptionPlansCreate />,
  },

  // Blog Managemnent
  { path: "/all-blogs", element: <AllBlog /> },
  { path: "/blogs-detail/:id", element: <BlogDetail /> },
  { path: "/create-blog", element: <CreateBlog /> },
  { path: "/blog-categories", element: <BlogCategories /> },
  { path: "/requests", element: <Requests /> },
  { path: "/requests/create", element: <RequestForm /> },
  { path: "/requests/:id", element: <RequestDetail /> },
  { path: "/blog-authors", element: <BlogAuthor /> },

  // Platform Settings
  { path: "/settings", element: <PlatformSettings /> },

  // Package Management
  { path: "/all-packages", element: <PackageList /> },
  { path: "/my-packages", element: <MyPackages /> },
  { path: "/package-detail/:id", element: <PackageDetail /> },
  { path: "/package-vendors", element: <PackageVendors /> },
  { path: "/package-vendors/:vendor", element: <VendorPackages /> },
  { path: "/package-categories", element: <CategoryManagement /> },
];

// ─────────────────────────────────────────────────────────────────────────────

// Suspense is inside LayoutWrapper so sidebar + header stay visible during page load
const LayoutWrapper = ({ children }) => (
  <Layout>
    <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
  </Layout>
);

const AppRoutes = () => (
  <Routes>
    {publicRoutes.map(({ path, element }) => (
      <Route key={path} path={path} element={element} />
    ))}
    {protectedRoutes.map(({ path, element }) => (
      <Route
        key={path}
        path={path}
        element={
          <PrivateRoute>
            <LayoutWrapper>{element}</LayoutWrapper>
          </PrivateRoute>
        }
      />
    ))}

    <Route
      path="/create-listing/:id?"
      element={
        <PrivateRoute>
          <LayoutWrapper>
            <CreateLayout />
          </LayoutWrapper>
        </PrivateRoute>
      }
    >
      <Route index element={<Navigate to="basic-info" replace />} />
      <Route path="basic-info" element={<BasicInfo />} />
      <Route path="location" element={<Location />} />
      <Route path="capacity" element={<Capacity />} />
      <Route path="amenities" element={<Amenities />} />
      <Route path="booking" element={<BookingDetail />} />
      <Route path="rules" element={<Rules />} />
      <Route path="events" element={<Events />} />
    </Route>
    <Route
      path="/create-event/:eventId?"
      element={
        <PrivateRoute>
          <LayoutWrapper>
            <CreateEventLayout />
          </LayoutWrapper>
        </PrivateRoute>
      }
    >
      <Route index element={<Navigate to="basic-information" replace />} />
      <Route path="basic-information" element={<BasicInformation />} />
      <Route path="media" element={<Media />} />
      <Route path="date-time" element={<DateAndTime />} />
      <Route path="venue-details" element={<EventVenueDetail />} />
      <Route path="seat-layout" element={<SeatLayout />} />
      <Route path="ticket-setup" element={<TicketSetup />} />
      <Route path="policies" element={<Policies />} />
    </Route>
       <Route
      path="/create-holiday-package/:packageId?"
      element={
        <PrivateRoute>
          <LayoutWrapper>
            <CreatePackageLayout />
          </LayoutWrapper>
        </PrivateRoute>
      }
    >
      <Route index element={<Navigate to="basic-info" replace />} />
      <Route path="basic-info" element={<PackageBasicInfo />} />
      <Route path="media" element={<PackageMedia />} />
      <Route path="itinerary" element={<PackageItinerary />} />
      <Route
        path="inclusions-exclusions"
        element={<PackageInclusionsExclusions />}
      />
      <Route path="pricing" element={<PackagePricing />} />
      {/* <Route path="add-ons" element={<PackageAddOns />} /> */}
      <Route
        path="cancellation-policy"
        element={<PackageCancellationPolicy />}
      />
    </Route>
    <Route path="*" element={<NotFound />} />
  </Routes>
);

export default AppRoutes;
